import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, catchError, throwError, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { 
  ScheduledActivity, 
  ActivityType, 
  UserAvailability, 
  ConflictCheckResult, 
  CreateActivityRequest, 
  UpdateActivityStatusRequest,
  CalendarEvent,
  ExportFilters,
  ExportStatistics,
  WeeklyAvailability,
  AvailabilitySlot,
  ActivityStatus,
  ActivityStatusOption,
  BulkOperationResult
} from '../interfaces/activity-schedule.interface';

@Injectable({
  providedIn: 'root'
})
export class ActivityScheduleService {
  private readonly baseUrl = `${environment.interop.basePath}api/activity-schedule`;
  
  // Reactive data streams
  private activitiesSubject = new BehaviorSubject<ScheduledActivity[]>([]);
  public activities$ = this.activitiesSubject.asObservable();
  
  private activityTypesSubject = new BehaviorSubject<ActivityType[]>([]);
  public activityTypes$ = this.activityTypesSubject.asObservable();
  
  private currentUserAvailabilitySubject = new BehaviorSubject<WeeklyAvailability>({});
  public currentUserAvailability$ = this.currentUserAvailabilitySubject.asObservable();
  
  // Cache for frequently used data
  private activityTypesCache: ActivityType[] | null = null;
  private cacheExpiry: { [key: string]: number } = {};
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  // ========================================
  // CALENDAR AND ACTIVITY RETRIEVAL
  // ========================================

  getCalendarActivities(userId: number, startDate: string, endDate: string): Observable<ScheduledActivity[]> {
    const params = new HttpParams()
      .set('user_id', userId.toString())
      .set('start_date', startDate)
      .set('end_date', endDate);

    console.log('ActivityScheduleService: Making API call to', `${this.baseUrl}/calendar`, 'with params:', { userId, startDate, endDate });

    return this.http.get<{ success: boolean; data: ScheduledActivity[]; message: string }>(
      `${this.baseUrl}/calendar`, { params }
    ).pipe(
      map(response => {
        console.log('ActivityScheduleService: Received response:', response);
        if (response.success) {
          this.activitiesSubject.next(response.data);
          return response.data;
        }
        throw new Error(response.message || 'Failed to load calendar activities');
      }),
      catchError(error => {
        console.error('ActivityScheduleService: API call failed:', error);
        return this.handleError(error);
      })
    );
  }

  getCerereActivities(cerereId: number): Observable<ScheduledActivity[]> {
    return this.http.get<{ success: boolean; data: ScheduledActivity[]; message: string }>(
      `${this.baseUrl}/cerere/${cerereId}/activities`
    ).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load cerere activities');
      }),
      catchError(this.handleError)
    );
  }

  getActivityDetails(activityId: number): Observable<ScheduledActivity> {
    return this.http.get<{ success: boolean; data: ScheduledActivity; message: string }>(
      `${this.baseUrl}/activity/${activityId}`
    ).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load activity details');
      }),
      catchError(this.handleError)
    );
  }

  // ========================================
  // ACTIVITY MANAGEMENT
  // ========================================

  createActivity(activityData: CreateActivityRequest): Observable<ScheduledActivity> {
    return this.http.post<{ success: boolean; data: ScheduledActivity; message: string }>(
      `${this.baseUrl}/activity`, activityData
    ).pipe(
      map(response => {
        if (response.success) {
          this.refreshActivitiesList();
          return response.data;
        }
        throw new Error(response.message || 'Failed to create activity');
      }),
      catchError(this.handleError)
    );
  }

  updateActivity(activityId: number, activityData: CreateActivityRequest): Observable<ScheduledActivity> {
    return this.http.put<{ success: boolean; data: ScheduledActivity; message: string }>(
      `${this.baseUrl}/activity/${activityId}`, activityData
    ).pipe(
      map(response => {
        if (response.success) {
          this.refreshActivitiesList();
          return response.data;
        }
        throw new Error(response.message || 'Failed to update activity');
      }),
      catchError(this.handleError)
    );
  }

  updateActivityStatus(activityId: number, statusData: UpdateActivityStatusRequest): Observable<ScheduledActivity> {
    return this.http.put<{ success: boolean; data: ScheduledActivity; message: string }>(
      `${this.baseUrl}/activity/${activityId}/status`, statusData
    ).pipe(
      map(response => {
        if (response.success) {
          this.refreshActivitiesList();
          return response.data;
        }
        throw new Error(response.message || 'Failed to update activity status');
      }),
      catchError(this.handleError)
    );
  }

  assignOperatorToActivity(activityId: number, assignmentData: any): Observable<ScheduledActivity> {
    return this.http.put<{ success: boolean; data: ScheduledActivity; message: string }>(
      `${this.baseUrl}/activity/${activityId}/assign`, assignmentData
    ).pipe(
      map(response => {
        if (response.success) {
          this.refreshActivitiesList();
          return response.data;
        }
        throw new Error(response.message || 'Failed to assign operator');
      }),
      catchError(this.handleError)
    );
  }

  // ========================================
  // AVAILABILITY MANAGEMENT
  // ========================================

  getUserAvailability(userId: number): Observable<WeeklyAvailability> {
    return this.http.get<{ success: boolean; data: WeeklyAvailability; message: string }>(
      `${this.baseUrl}/user/${userId}/availability`
    ).pipe(
      map(response => {
        if (response.success) {
          this.currentUserAvailabilitySubject.next(response.data);
          return response.data;
        }
        throw new Error(response.message || 'Failed to load availability');
      }),
      catchError(this.handleError)
    );
  }

  setUserAvailability(userId: number, availability: AvailabilitySlot[]): Observable<WeeklyAvailability> {
    return this.http.post<{ success: boolean; data: WeeklyAvailability; message: string }>(
      `${this.baseUrl}/user/${userId}/availability`, { availability }
    ).pipe(
      map(response => {
        if (response.success) {
          this.currentUserAvailabilitySubject.next(response.data);
          return response.data;
        }
        throw new Error(response.message || 'Failed to save availability');
      }),
      catchError(this.handleError)
    );
  }

  setUserAvailabilityWithDateRange(userId: number, requestData: any): Observable<WeeklyAvailability> {
    return this.http.post<{ success: boolean; data: WeeklyAvailability; message: string }>(
      `${this.baseUrl}/user/${userId}/availability`, requestData
    ).pipe(
      map(response => {
        if (response.success) {
          this.currentUserAvailabilitySubject.next(response.data);
          return response.data;
        }
        throw new Error(response.message || 'Failed to save availability');
      }),
      catchError(this.handleError)
    );
  }

  // ========================================
  // CONFLICT CHECKING
  // ========================================

  checkConflicts(
    userId: number | null, 
    scheduledDate: string, 
    startTime: string, 
    endTime: string, 
    excludeActivityId?: number
  ): Observable<ConflictCheckResult> {
    const requestData: any = {
      user_id: userId,
      scheduled_date: scheduledDate,
      start_time: startTime,
      end_time: endTime
    };

    if (excludeActivityId) {
      requestData.exclude_activity_id = excludeActivityId;
    }

    return this.http.post<{ success: boolean; data: ConflictCheckResult; message: string }>(
      `${this.baseUrl}/conflicts/check`, requestData
    ).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to check conflicts');
      }),
      catchError(this.handleError)
    );
  }

  // ========================================
  // ACTIVITY TYPES MANAGEMENT
  // ========================================

  getActivityTypes(search?: string): Observable<ActivityType[]> {
    // Check cache first
    const cacheKey = `activity_types_${search || ''}`;
    const now = Date.now();
    
    if (this.activityTypesCache && 
        this.cacheExpiry[cacheKey] && 
        now < this.cacheExpiry[cacheKey]) {
      return of(this.activityTypesCache);
    }

    const params = new HttpParams();
    if (search) {
      params.set('search', search);
    }

    return this.http.get<{ success: boolean; data: ActivityType[]; message: string }>(
      `${this.baseUrl}/activity-types`, { params }
    ).pipe(
      map(response => {
        if (response.success) {
          this.activityTypesCache = response.data;
          this.cacheExpiry[cacheKey] = now + this.CACHE_DURATION;
          this.activityTypesSubject.next(response.data);
          return response.data;
        }
        throw new Error(response.message || 'Failed to load activity types');
      }),
      catchError(this.handleError)
    );
  }

  createActivityType(name: string, description?: string): Observable<ActivityType> {
    return this.http.post<{ success: boolean; data: ActivityType; message: string }>(
      `${this.baseUrl}/activity-types`, { name, description }
    ).pipe(
      map(response => {
        if (response.success) {
          this.clearActivityTypesCache();
          return response.data;
        }
        throw new Error(response.message || 'Failed to create activity type');
      }),
      catchError(this.handleError)
    );
  }

  // ========================================
  // EXPORT FUNCTIONALITY
  // ========================================

  exportToPDF(filters: ExportFilters, format: 'list' | 'calendar' = 'list'): Observable<Blob> {
    const requestData = { ...filters, format };
    
    return this.http.post(`${this.baseUrl}/export/pdf`, requestData, {
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  exportToExcel(filters: ExportFilters): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/export/excel`, filters, {
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  exportUserSchedule(userId: number, startDate: string, endDate: string): Observable<Blob> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    return this.http.get(`${this.baseUrl}/export/user/${userId}/schedule`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  getExportStatistics(filters: ExportFilters): Observable<ExportStatistics> {
    return this.http.post<{ success: boolean; data: ExportStatistics; message: string }>(
      `${this.baseUrl}/export/statistics`, filters
    ).pipe(
      map(response => {
        if (response.success) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to get statistics');
      }),
      catchError(this.handleError)
    );
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  // Convert ScheduledActivity to CalendarEvent for calendar display
  convertToCalendarEvents(activities: ScheduledActivity[]): CalendarEvent[] {
    return activities.map(activity => ({
      id: activity.id,
      title: activity.title,
      start: new Date(`${activity.scheduled_date}T${activity.start_time}`),
      end: new Date(`${activity.scheduled_date}T${activity.end_time}`),
      color: this.getStatusColor(activity.status),
      activity: activity,
      extendedProps: {
        status: activity.status,
        type: activity.activity_type?.name || 'Unknown',
        location: activity.location,
        assignedUser: activity.user?.full_name
      }
    }));
  }

  // Get status options with Romanian labels
  getStatusOptions(): ActivityStatusOption[] {
    return [
      { key: 'scheduled', label: 'Programată', color: '#007bff' },
      { key: 'completed', label: 'Finalizată', color: '#28a745' },
      { key: 'cancelled', label: 'Anulată', color: '#dc3545' },
      { key: 'rescheduled', label: 'Reprogramată', color: '#ffc107' },
      { key: 'no_show', label: 'Absent', color: '#6c757d' }
    ];
  }

  // Get color for activity status
  private getStatusColor(status: ActivityStatus): string {
    const statusOption = this.getStatusOptions().find(option => option.key === status);
    return statusOption?.color || '#6c757d';
  }

  // Get Romanian day names
  getDayNames(): { [key: number]: string } {
    return {
      1: 'Luni',
      2: 'Marți', 
      3: 'Miercuri',
      4: 'Joi',
      5: 'Vineri',
      6: 'Sâmbătă',
      7: 'Duminică'
    };
  }

  // Format time for display
  formatTime(time: string): string {
    return time.substring(0, 5); // HH:mm from HH:mm:ss
  }

  // Format date for display
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('ro-RO');
  }

  // Generate time slots for dropdowns (10-minute intervals)
  generateTimeSlots(): string[] {
    const slots: string[] = [];
    for (let hour = 8; hour <= 18; hour++) {
      for (let minute = 0; minute < 60; minute += 10) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  }

  // Download blob as file
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  // ========================================
  // CACHE AND REFRESH METHODS
  // ========================================

  private clearActivityTypesCache(): void {
    this.activityTypesCache = null;
    this.cacheExpiry = {};
  }

  private refreshActivitiesList(): void {
    // Trigger refresh of current activities list
    // This would typically be called after create/update/delete operations
    const currentActivities = this.activitiesSubject.value;
    if (currentActivities.length > 0) {
      // Could implement smart refresh logic here
    }
  }

  // ========================================
  // BULK OPERATIONS
  // ========================================

  bulkRescheduleActivities(
    activityIds: number[], 
    newDate: string, 
    newStartTime: string, 
    newEndTime: string, 
    reason: string
  ): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/reschedule`, {
      activity_ids: activityIds,
      new_date: newDate,
      new_start_time: newStartTime,
      new_end_time: newEndTime,
      reason
    }).pipe(
      catchError(error => {
        console.error('Bulk reschedule error:', error);
        return of({
          operation: 'reschedule',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la reprogramarea în masă']
        });
      })
    );
  }

  bulkCancelActivities(activityIds: number[], reason: string): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/cancel`, {
      activity_ids: activityIds,
      reason
    }).pipe(
      catchError(error => {
        console.error('Bulk cancel error:', error);
        return of({
          operation: 'cancel',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la anularea în masă']
        });
      })
    );
  }

  bulkCompleteActivities(
    activityIds: number[], 
    notes: string, 
    completionDate: string
  ): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/complete`, {
      activity_ids: activityIds,
      notes,
      completion_date: completionDate
    }).pipe(
      catchError(error => {
        console.error('Bulk complete error:', error);
        return of({
          operation: 'complete',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la finalizarea în masă']
        });
      })
    );
  }

  bulkAssignActivities(
    activityIds: number[], 
    newOperatorId: number, 
    notes: string
  ): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/assign`, {
      activity_ids: activityIds,
      assigned_user_id: newOperatorId,
      notes
    }).pipe(
      catchError(error => {
        console.error('Bulk assign error:', error);
        return of({
          operation: 'assign',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la reasignarea în masă']
        });
      })
    );
  }

  bulkChangeActivityType(
    activityIds: number[], 
    newTypeId: number, 
    reason: string
  ): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/change-type`, {
      activity_ids: activityIds,
      activity_type_id: newTypeId,
      reason
    }).pipe(
      catchError(error => {
        console.error('Bulk change type error:', error);
        return of({
          operation: 'change_type',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la schimbarea tipului în masă']
        });
      })
    );
  }

  bulkAddNotes(
    activityIds: number[], 
    notes: string, 
    category: string = 'general'
  ): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/add-notes`, {
      activity_ids: activityIds,
      notes,
      category
    }).pipe(
      catchError(error => {
        console.error('Bulk add notes error:', error);
        return of({
          operation: 'add_notes',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la adăugarea notițelor în masă']
        });
      })
    );
  }

  bulkExportActivities(
    activityIds: number[], 
    format: string, 
    includeDetails: boolean, 
    includeHistory: boolean
  ): Observable<BulkOperationResult> {
    return this.http.post<BulkOperationResult>(`${this.baseUrl}/bulk/export`, {
      activity_ids: activityIds,
      format,
      include_details: includeDetails,
      include_history: includeHistory
    }).pipe(
      catchError(error => {
        console.error('Bulk export error:', error);
        return of({
          operation: 'export',
          total: activityIds.length,
          successCount: 0,
          failureCount: activityIds.length,
          errors: [error.message || 'Eroare la exportul în masă']
        });
      })
    );
  }

  // ========================================
  // REPORTS AND ANALYTICS
  // ========================================

  getActivitiesByDateRange(
    userId: number,
    startDate: string,
    endDate: string,
    activityTypes?: number[],
    statuses?: string[]
  ): Observable<ScheduledActivity[]> {
    let params = new HttpParams()
      .set('user_id', userId.toString())
      .set('start_date', startDate)
      .set('end_date', endDate);
    
    if (activityTypes && activityTypes.length > 0) {
      params = params.set('activity_types', activityTypes.join(','));
    }
    
    if (statuses && statuses.length > 0) {
      params = params.set('statuses', statuses.join(','));
    }
    
    return this.http.get<ScheduledActivity[]>(`${this.baseUrl}/reports/activities-range`, { params })
      .pipe(
        catchError(error => {
          console.error('Error loading activities by date range:', error);
          return of([]);
        })
      );
  }

  // ========================================
  // ERROR HANDLING
  // ========================================

  private handleError(error: any): Observable<never> {
    let errorMessage = 'A apărut o eroare neașteptată.';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Eroare: ${error.error.message}`;
    } else {
      // Server-side error
      if (error.status === 0) {
        errorMessage = 'Nu se poate conecta la server. Verificați conexiunea la internet.';
      } else if (error.status === 401) {
        errorMessage = 'Nu sunteți autentificat. Vă rugăm să vă autentificați din nou.';
      } else if (error.status === 403) {
        errorMessage = 'Nu aveți permisiunea necesară pentru această operațiune.';
      } else if (error.status === 404) {
        errorMessage = 'Resursa solicitată nu a fost găsită.';
      } else if (error.status >= 500) {
        errorMessage = 'Eroare de server. Vă rugăm să încercați din nou mai târziu.';
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }
    
    console.error('ActivityScheduleService Error:', error);
    return throwError(() => new Error(errorMessage));
  }

  // ========================================
  // EXPORT METHODS
  // ========================================

  exportActivities(exportData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/export`, exportData, {
      observe: 'response'
    }).pipe(
      map(response => {
        // Handle different response types
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/pdf')) {
          // PDF file response
          return {
            success: true,
            file_content: response.body,
            content_type: 'application/pdf'
          };
        } else if (contentType && contentType.includes('text/csv')) {
          // CSV file response
          return {
            success: true,
            file_content: response.body,
            content_type: 'text/csv'
          };
        } else if (response.body && typeof response.body === 'object') {
          // JSON response with download URL or data
          return response.body;
        } else {
          // Raw text/content
          return {
            success: true,
            file_content: response.body,
            content_type: contentType || 'text/plain'
          };
        }
      }),
      catchError(this.handleError)
    );
  }
}